import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Box, BoxProps } from '@apia/theme';
import ReactFocusLock, { FreeFocusInside } from 'react-focus-lock';
import useCombinedRefs from '../../../../hooks/useCombinedRefs';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';

export interface IOverlay extends BoxProps {
  children: React.ReactNode;
  className?: string;
  onOverlayClicked: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Permite pasar un callback que será llamado una vez que el overlay termine
   * de cerrarse.
   */
  onExited: () => void;
  isOpen?: boolean;
  shouldAvoidInitialFocusTrap?: boolean;
  stretch?: boolean;
  transitionDuration?: number;
}

const Overlay = (
  {
    children,
    className = '',
    onExited,
    onOverlayClicked,
    isOpen: open = false,
    shouldAvoidInitialFocusTrap,
    stretch,
    transitionDuration = 150,
    ...props
  }: IOverlay,
  ref: React.ForwardedRef<unknown>,
): JSX.Element => {
  const innerRef = React.useRef<HTMLDivElement>(null);
  const assignRefs = useCombinedRefs(innerRef, ref);
  useBodyScrollLock(open);
  return (
    <CSSTransition
      in={open}
      timeout={transitionDuration}
      onExited={onExited}
      classNames="overlayAnimation"
      appear
      unmountOnExit
    >
      <Box
        className={`${className} overlay`}
        ref={assignRefs}
        {...props}
        data-type="overlay"
        role="presentation"
        onClick={React.useCallback(
          (ev: React.MouseEvent<HTMLDivElement>) => {
            if (ev.target === innerRef.current) onOverlayClicked(ev);
          },
          [onOverlayClicked],
        )}
        {...window.getVariant(
          props.variant ??
            `layout.common.modals.overlay${stretch ? '--stretch' : ''}`,
        )}
      >
        <ReactFocusLock
          autoFocus={shouldAvoidInitialFocusTrap ? false : undefined}
          returnFocus
          crossFrame={false}
        >
          <FreeFocusInside>{children}</FreeFocusInside>
        </ReactFocusLock>
      </Box>
    </CSSTransition>
  );
};

export default React.forwardRef(Overlay);
